import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  DeleteFilled,
  EditFilled,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Form,
  Input,
  message,
  Modal,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';

import StreamEnvironmentService from '../../../api/stream-environment';
import { ApiError, handleError } from '../../../api/base';
import {
  initialStreamEnvironmentFormValues,
  StreamEnvironmentFormValues,
  StreamEnvironmentModel,
} from '../../../models/stream-environment';
import { useAuth } from '../../../hooks/useAuth';
import {
  canViewStudioEnvironments,
  UserPermissions,
} from '../../../util';
import Button from '../../elements/Button';
import NoPermission from '../../elements/NoPermission';
import ViewWrapper from '../../elements/ViewWrapper';
import './StudioEnvironmentsView.less';

const { Text, Title } = Typography;

const StudioEnvironmentsView = () => {
  const [streamEnvironments, setStreamEnvironments] = useState<
    StreamEnvironmentModel[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [editingEnvironment, setEditingEnvironment] =
    useState<StreamEnvironmentModel | null>(null);
  const [form] = Form.useForm<StreamEnvironmentFormValues>();
  const { hasPermission } = useAuth();

  const canView = canViewStudioEnvironments(hasPermission);
  const canManage = hasPermission(UserPermissions.StudioAdmin);

  const sortedEnvironments = useMemo(
    () =>
      [...streamEnvironments].sort((a, b) => {
        const aDeleted = !!a.deleted_at;
        const bDeleted = !!b.deleted_at;
        if (aDeleted !== bDeleted) {
          return aDeleted ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      }),
    [streamEnvironments]
  );

  const onError = useCallback((err: ApiError) => {
    handleError(err);
  }, []);

  const fetchEnvironments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await StreamEnvironmentService().listStreamEnvironments(
        '?_batch=0'
      );
      setStreamEnvironments(res.data || []);
    } catch (err: any) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    if (canView) {
      fetchEnvironments();
    }
  }, [canView, fetchEnvironments]);

  const openCreateModal = useCallback(() => {
    setEditingEnvironment(null);
    form.setFieldsValue(initialStreamEnvironmentFormValues);
    setIsModalVisible(true);
  }, [form]);

  const openEditModal = useCallback(
    (environment: StreamEnvironmentModel) => {
      if (environment.deleted_at) return;
      setEditingEnvironment(environment);
      form.setFieldsValue({
        name: environment.name,
        description: environment.description || '',
        aws_region: environment.aws_region,
        aws_stack: environment.aws_stack,
        aws_fleet: environment.aws_fleet,
        aws_app_ids: environment.aws_app_ids || 'studio',
        is_default: Boolean(environment.is_default),
        active: Boolean(environment.active),
      });
      setIsModalVisible(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setIsModalVisible(false);
    setEditingEnvironment(null);
    form.resetFields();
  }, [form]);

  const saveEnvironment = useCallback(
    async (values: StreamEnvironmentFormValues) => {
      setSaving(true);
      try {
        if (editingEnvironment) {
          const res = await StreamEnvironmentService().updateStreamEnvironment(
            editingEnvironment.uuid,
            values
          );
          setStreamEnvironments((prev) =>
            prev.map((item) =>
              item.uuid === editingEnvironment.uuid ? res.data : item
            )
          );
          message.success(`Environment '${values.name}' updated.`);
        } else {
          const res = await StreamEnvironmentService().createStreamEnvironment(
            values
          );
          setStreamEnvironments((prev) => [res.data, ...prev]);
          message.success(`Environment '${values.name}' created.`);
        }
        closeModal();
      } catch (err: any) {
        onError(err);
      } finally {
        setSaving(false);
      }
    },
    [closeModal, editingEnvironment, onError]
  );

  const confirmDeleteEnvironment = useCallback(
    (environment: StreamEnvironmentModel) => {
      if (environment.deleted_at) return;
      Modal.confirm({
        title: `Delete environment '${environment.name}'?`,
        icon: <ExclamationCircleOutlined />,
        content: 'You will not be able to edit this environment after deletion.',
        okText: 'Delete',
        onOk: async () => {
          try {
            await StreamEnvironmentService().deleteStreamEnvironment(
              environment.uuid
            );
            setStreamEnvironments((prev) =>
              prev.map((item) =>
                item.uuid === environment.uuid
                  ? { ...item, deleted_at: new Date().toISOString() }
                  : item
              )
            );
            message.success(`Environment '${environment.name}' deleted.`);
          } catch (err: any) {
            onError(err);
          }
        },
      });
    },
    [onError]
  );

  const columns: ColumnsType<StreamEnvironmentModel> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record) => (
          <div>
            <Text delete={!!record.deleted_at}>{name}</Text>
            {record.description ? (
              <div className="studio-environment-description">
                <Text type="secondary">{record.description}</Text>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: 'Region',
        dataIndex: 'aws_region',
        key: 'aws_region',
        render: (value: string) => <Text code>{value}</Text>,
      },
      {
        title: 'Stack',
        dataIndex: 'aws_stack',
        key: 'aws_stack',
        render: (value: string) => <Text code>{value}</Text>,
      },
      {
        title: 'Fleet',
        dataIndex: 'aws_fleet',
        key: 'aws_fleet',
        render: (value: string) => <Text code>{value}</Text>,
      },
      {
        title: 'App IDs',
        dataIndex: 'aws_app_ids',
        key: 'aws_app_ids',
        render: (value: string) => <Text code>{value}</Text>,
      },
      {
        title: 'Default',
        dataIndex: 'is_default',
        key: 'is_default',
        render: (value: boolean | number) => (
          <Text>{Boolean(value) ? 'Yes' : 'No'}</Text>
        ),
      },
      {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        render: (value: boolean | number) => (
          <Text>{Boolean(value) ? 'Yes' : 'No'}</Text>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, record) =>
          record.deleted_at ? (
            <Text type="secondary">Deleted</Text>
          ) : (
            <Text>Available</Text>
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_, record) => {
          const isDeleted = !!record.deleted_at;
          if (!canManage) return null;
          return (
            <Space direction="horizontal" size="middle">
              <Tooltip title={isDeleted ? 'Deleted environments cannot be edited' : 'Edit'}>
                <EditFilled
                  onClick={() => !isDeleted && openEditModal(record)}
                  className={`studio-environments-action-icon ${isDeleted ? 'disabled' : ''}`}
                />
              </Tooltip>
              <Tooltip
                title={isDeleted ? 'Already deleted' : 'Delete'}
              >
                <DeleteFilled
                  onClick={() => !isDeleted && confirmDeleteEnvironment(record)}
                  className={`studio-environments-action-icon ${isDeleted ? 'disabled' : ''}`}
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    [canManage, confirmDeleteEnvironment, openEditModal]
  );

  if (!canView) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={
        <Space direction="horizontal">
          <Title level={3}>Studio Environments</Title>
          <Badge
            size="default"
            count={sortedEnvironments.length}
            className="total-count-badge"
          />
        </Space>
      }
      headerSectionRight={
        canManage ? (
          <Button type="primary" onClick={openCreateModal}>
            Create Environment
            <PlusOutlined />
          </Button>
        ) : undefined
      }
    >
      <Table
        loading={loading}
        rowKey={(record) => record.uuid}
        dataSource={sortedEnvironments}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        rowClassName={(record) =>
          record.deleted_at ? 'studio-environment-row-deleted' : ''
        }
      />

      <Modal
        title={editingEnvironment ? 'Edit Studio Environment' : 'Create Studio Environment'}
        visible={isModalVisible}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={editingEnvironment ? 'Save' : 'Create'}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialStreamEnvironmentFormValues}
          onFinish={saveEnvironment}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required.' }]}
          >
            <Input placeholder="Environment name" />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ max: 255, message: 'Description cannot exceed 255 characters.' }]}
          >
            <Input.TextArea
              placeholder="Optional description"
              rows={2}
              maxLength={255}
              showCount
            />
          </Form.Item>
          <Form.Item
            label="AWS Region"
            name="aws_region"
            rules={[{ required: true, message: 'AWS region is required.' }]}
          >
            <Input placeholder="us-east-2" />
          </Form.Item>
          <Form.Item
            label="AWS Stack"
            name="aws_stack"
            rules={[{ required: true, message: 'AWS stack is required.' }]}
          >
            <Input placeholder="stack-name" />
          </Form.Item>
          <Form.Item
            label="AWS Fleet"
            name="aws_fleet"
            rules={[{ required: true, message: 'AWS fleet is required.' }]}
          >
            <Input placeholder="fleet-name" />
          </Form.Item>
          <Form.Item
            label="AWS App IDs"
            name="aws_app_ids"
            rules={[{ required: true, message: 'AWS app ids are required.' }]}
          >
            <Input placeholder="studio" />
          </Form.Item>
          <Form.Item label="Is Default" name="is_default" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Active" name="active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </ViewWrapper>
  );
};

export default StudioEnvironmentsView;
